<template>
    <div class="entry-history">
        <loading-indicator v-if="!historyEntry"/>
        <template v-else>
            <slot name="before"/>

            <div v-if="historyEntry.edited && historyEntry.edited !== historyEntry.created" class="flex flex-vertical-middle margin-bottom-15">
                <person-box v-if="historyEntry.history_editor_name" :name="historyEntry.history_editor_name">
                    <template #name="{username}">
                        <i>{{username}}</i>
                    </template>

                    <template #default>Letzte Änderung am {{dateHelper('d.m.Y, H:i', historyEntry.edited)}} Uhr</template>
                </person-box>
                <person-box-user v-else :id="historyEntry.editor">
                    <template #name="{username}">
                        <span>{{username}}</span>
                    </template>

                    <template #default>Letzte Änderung am {{dateHelper('d.m.Y, H:i', historyEntry.edited)}} Uhr</template>
                </person-box-user>

                <div
                    v-if="showEditHistory"
                    class="padding-5 margin-left-15 pointer"
                    v-interact="showEditHistoryPopup"
                    title="Bearbeitungshistorie ansehen"
                    role="button">
                    <i class="material-icons" aria-hidden="true" role="img">list</i>
                </div>
            </div>

            <person-box v-if="historyEntry.history_creator_name" :name="historyEntry.history_creator_name">
                <template #name="{username}">
                    <i>{{username}}</i>
                </template>

                <template #default>Erstellt am {{dateHelper('d.m.Y, H:i', historyEntry.created)}} Uhr</template>
            </person-box>
            <person-box-user v-else-if="historyEntry.creator && historyEntry.creator >= -1" :id="historyEntry.creator">
                <template #name="{username}">
                    <span>{{username}}</span>
                </template>

                <template #default>Erstellt am {{dateHelper('d.m.Y, H:i', historyEntry.created)}} Uhr</template>
            </person-box-user>
            <i v-else-if="!historyEntry.editor && !historyEntry.edited">
                Es sind keine Informationen verfügbar.
            </i>
        </template>
    </div>
</template>

<script setup lang="ts">
    import axios from 'axios';
    import EntryHistoryEditHistoryPopup from '@/components/EntryHistoryEditHistoryPopup.vue';
    import {hasModule} from '@/library/intranet';
    import {Popup} from '@/library/popup';
    import {isAllowedModule} from '@/library/user';
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import {errorHandler} from '@/utils/portuna';
    import dateHelper from '../utils/portuna/dateHelper';
    import PersonBox from './PersonBox.vue';
    import PersonBoxUser from './PersonBoxUser.vue';
    import {ref, computed} from 'vue';

    const props = withDefaults(
        defineProps<{
            history: IEntryHistory extends infer T ? T & object : never;
            module?: string;
            namespace?: string;
            entryId?: number;
            fetchByLog?: boolean;
        }>(),
        {
            module: '',
            namespace: '',
            entryId: 0,
            fetchByLog: false,
        },
    );

    const logHistoryEntry = ref<Nullable<ILogEditHistoryEntryData>>(null);

    const historyEntry = computed((): Nullable<IEntryHistory> =>
    {
        return (props.fetchByLog && logHistoryEntry.value) || props.history;
    });

    const editHistory = computed((): Nullable<IEntryHistoryEntry[]> =>
    {
        return historyEntry.value?.edit_history || null;
    });

    const showEditHistory = computed((): boolean =>
    {
        return (
            !!editHistory.value ||
            (hasModule('log') &&
                isAllowedModule('log') &&
                !!props.module &&
                !!props.namespace &&
                !!props.entryId)
        );
    });

    const showEditHistoryPopup = (): void =>
    {
        if (!showEditHistory.value)
        {
            return;
        }
        let popup = new Popup({
            title: 'Bearbeitungshistorie',
            titleIcon: 'list',
            size: 'small',
            component: EntryHistoryEditHistoryPopup,
            componentProps: {
                history: historyEntry.value,
                module: props.module,
                namespace: props.namespace,
                entryId: props.entryId,
                entries: editHistory.value,
            },
        });

        popup.open();
    };

    const fetchLog = (): void =>
    {
        axios
        .get(
            '/log/fetch-entry-history/module/' +
            props.module +
            '/namespace/' +
            props.namespace +
            '/entry-id/' +
            props.entryId,
        )
        .then(({data: {data}}) =>
        {
            logHistoryEntry.value = data;
        })
        .catch(errorHandler);
    };

    if (props.fetchByLog && showEditHistory.value && !editHistory.value)
    {
        fetchLog();
    }
</script>